import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppSettingsSidebar } from "@/components/app-settings-sidebar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeSettings } from "@/components/ThemeSettings";
import { useTheme } from "@/contexts/ThemeContext";
import { SETTINGS_TABS, SettingsTab } from "@/config/settingsTabs";
import { getUserInfo } from "@/services/userService";
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';
import { getJoinRequests } from "@/services/authDomainService";
import { useTenancy } from "@/contexts/TenancyContext";

import BillingSection from "./sections/BillingSection";
import CompanySection from "./sections/CompanySection";
import GeneralSection from "./sections/GeneralSection";
import AccountSection from "./sections/AccountSection";
import FeaturesSection from "./sections/FeaturesSection";
import IntegrationsSection from "./sections/IntegrationsSection";
import ModulesSection from "./sections/ModulesSection";
import TrustPageSection from "./sections/TrustPageSection";
import PartnersSection from "./sections/PartnersSection";
import MSPSettingsSection from "./sections/MSPSettingsSection";
import SecuritySection from "./sections/SecuritySection";
import MenuSettingsSection from "./sections/MenuSettingsSection";
import DiagnosticsSection from "./sections/DiagnosticsSection";
import CalendarSettingsSection from "./sections/CalendarSettingsSection";
import DeveloperSection from "./sections/DeveloperSection";
import AuthorisedDomainsSection from "./sections/AuthorisedDomainsSection";
import SSOProvidersSection from "./sections/SSOProvidersSection";
import JoinRequestsSection from "./sections/JoinRequestsSection";

type TabContentConfig = {
  component: React.ReactNode;
  fullWidth?: boolean;
};

const tabContent: Record<string, TabContentConfig> = {
  account: { component: <AccountSection /> },
  theme: { component: <ThemeSettings /> },
  security: { component: <SecuritySection /> },
  general: { component: <GeneralSection /> },
  billing: { component: <BillingSection /> },
  integrations: { component: <IntegrationsSection />, fullWidth: true },
  modules: { component: <ModulesSection /> },
  features: { component: <FeaturesSection /> },
  trustPage: { component: <TrustPageSection /> },
  partners: { component: <PartnersSection /> },
  menus: { component: <MenuSettingsSection /> },
  calendar: { component: <CalendarSettingsSection /> },
  mspSettings: { component: <div className="w-full"><MSPSettingsSection /></div> },
  diagnostics: { component: <DiagnosticsSection /> },
  developer: { component: <DeveloperSection /> },
  authorisedDomains: { component: <AuthorisedDomainsSection /> },
  ssoProviders: { component: <SSOProvidersSection /> },
  joinRequests: { component: <JoinRequestsSection /> },
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState("account");
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTenancy } = useTenancy();

  // Define required roles for each tab (empty array means no role required)
  const TAB_ROLES: Record<string, string[]> = {
    diagnostics: ['bugTester'],
    mspSettings: ['msp'],
  };

  const [userRoles, setUserRoles] = useState<Record<string, boolean>>({});
  const [rolesChecked, setRolesChecked] = useState(false);
  const [pendingJoinRequestsCount, setPendingJoinRequestsCount] = useState(0);

  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  // Filter tabs based on user roles and add badge counts
  const filteredTabs = useMemo(() => {
    return SETTINGS_TABS.map(tab => {
      // If this is a group with options, filter the options
      if (tab.type === 'group' && tab.options) {
        const filteredOptions = tab.options.filter(option => {
          const requiredRoles = TAB_ROLES[option.key!];
          
          if (!rolesChecked) {
            // Before roles are checked, hide tabs that require roles
            return !requiredRoles;
          }
          
          // After roles are checked, show tabs the user has access to
          return !requiredRoles || requiredRoles.some(role => userRoles[role]);
        }).map(option => {
          // Add badge count for joinRequests tab
          if (option.key === 'joinRequests') {
            return { ...option, badge: pendingJoinRequestsCount };
          }
          return option;
        });
        
        // Only include the group if it has visible options
        return filteredOptions.length > 0 ? { ...tab, options: filteredOptions } : null;
      }
      
      // For non-group tabs (backwards compatibility)
      const requiredRoles = TAB_ROLES[tab.key!];
      if (!rolesChecked) {
        return !requiredRoles ? tab : null;
      }
      return !requiredRoles || requiredRoles.some(role => userRoles[role]) ? tab : null;
    }).filter(tab => tab !== null) as SettingsTab[];
  }, [rolesChecked, userRoles, pendingJoinRequestsCount]);

  // Function to handle tab changes
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Update URL when tab changes
    navigate(`/settings/${tabKey}`);
  };

  // Fetch user info to determine role-based tab visibility
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info: any = await getUserInfo();
        if (mounted && info?.roles) {
          setUserRoles(info.roles);
        }
      } catch (e) {
        console.error('Error loading user info:', e);
      } finally {
        if (mounted) setRolesChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch pending join requests count
  useEffect(() => {
    if (!activeTenancy?.id) return;
    
    let mounted = true;
    (async () => {
      try {
        const requests = await getJoinRequests(activeTenancy.id!, 'pending');
        if (mounted) {
          setPendingJoinRequestsCount(requests.length);
        }
      } catch (e) {
        console.error('Error loading pending join requests:', e);
      }
    })();
    return () => { mounted = false; };
  }, [activeTenancy?.id]);

  // Update active tab based on URL path
  useEffect(() => {
    if (!rolesChecked) return;
    
    const path = location.pathname;
    const section = path.split('/').pop();

    // If the section exists in our tabContent and user has required roles, set it as active
    if (section && section !== 'settings' && tabContent[section]) {
      const requiredRoles = TAB_ROLES[section];
      const hasAccess = !requiredRoles || requiredRoles.some(role => userRoles[role]);
      
      if (!hasAccess) {
        // Redirect to first available tab if user doesn't have access
        const firstAvailableTab = filteredTabs[0]?.key || 'account';
        setActiveTab(firstAvailableTab);
        navigate(`/settings/${firstAvailableTab}`);
        navigate('/settings/account', { replace: true });
      } else {
        setActiveTab(section);
      }
    } else if (path === '/settings') {
      // Default to account when just /settings is accessed
      setActiveTab('account');
    }
  }, [location.pathname, userRoles, rolesChecked, filteredTabs, navigate]);

  // Tour implementation
  useEffect(() => {
    setTourPage('settings');
  }, [setTourPage]);

  // Tour configuration for Settings - dynamic based on available tabs
  useEffect(() => {
    // Build tour steps dynamically based on available tabs
    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">Settings</h3>
            <p>This is your settings page. It contains various configuration options for your tenant.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.SETTINGS_PAGE,
        position: "bottom" as const,
      }
    ];

    // Add sidebar navigation step
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Settings Navigation</h3>
          <p>Use this sidebar to navigate between different settings sections.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.SETTINGS_SIDEBAR,
      position: "right" as const,
    });

    // Add steps for visible tabs
    const visibleTabs = filteredTabs.filter(tab => tab.type === 'tab');
    
    visibleTabs.forEach((tab, index) => {
      let stepId = '';
      let stepTitle = '';
      
      switch(tab.key) {
        case 'account':
          stepId = TOUR_STEP_IDS.ACCOUNT_SECTION;
          stepTitle = 'Account Settings';
          break;
        case 'theme':
          stepId = TOUR_STEP_IDS.THEME_SECTION;
          stepTitle = 'Theme Settings';
          break;
        case 'security':
          stepId = TOUR_STEP_IDS.SECURITY_SECTION;
          stepTitle = 'Security Settings';
          break;
        case 'general':
          stepId = TOUR_STEP_IDS.GENERAL_SECTION;
          stepTitle = 'General Settings';
          break;
        case 'billing':
          stepId = TOUR_STEP_IDS.BILLING_SECTION;
          stepTitle = 'Billing Settings';
          break;
        case 'integrations':
          stepId = TOUR_STEP_IDS.INTEGRATIONS_SECTION;
          stepTitle = 'Integrations';
          break;
        case 'modules':
          stepId = TOUR_STEP_IDS.MODULES_SECTION;
          stepTitle = 'Module Settings';
          break;
        case 'features':
          stepId = TOUR_STEP_IDS.FEATURES_SECTION;
          stepTitle = 'Feature Flags';
          break;
        case 'trustPage':
          stepId = TOUR_STEP_IDS.TRUST_PAGE_SECTION;
          stepTitle = 'Trust Page Settings';
          break;
        case 'partners':
          stepId = TOUR_STEP_IDS.PARTNERS_SECTION;
          stepTitle = 'Partner Settings';
          break;
        case 'menus':
          stepId = TOUR_STEP_IDS.MENUS_SECTION;
          stepTitle = 'Menu Settings';
          break;
        case 'calendar':
          stepId = TOUR_STEP_IDS.CALENDAR_SECTION;
          stepTitle = 'Calendar Settings';
          break;
        case 'mspSettings':
          stepId = TOUR_STEP_IDS.MSP_SETTINGS_SECTION;
          stepTitle = 'MSP Settings';
          break;
        case 'diagnostics':
          stepId = TOUR_STEP_IDS.DIAGNOSTICS_SECTION;
          stepTitle = 'Diagnostics';
          break;
        case 'developer':
          stepId = TOUR_STEP_IDS.DEVELOPER_SECTION;
          stepTitle = 'Developer Options';
          break;
        default:
          return; // Skip if no matching ID
      }

      // Only add step if we're currently on that tab
      if (activeTab === tab.key) {
        steps.push({
          content: (
            <div>
              <h3 className="font-semibold text-lg mb-2">{stepTitle}</h3>
              <p>Configure your {stepTitle.toLowerCase()} here.</p>
            </div>
          ),
          selectorId: stepId,
          position: "top" as const,
        });
      }
    });

    console.log('Settings tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted && rolesChecked) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage, activeTab, filteredTabs, rolesChecked]);

  return (
    <div id={TOUR_STEP_IDS.SETTINGS_PAGE} className="settings-page-wrapper flex w-full">
      {/* Settings sidebar */}
      <div id={TOUR_STEP_IDS.SETTINGS_SIDEBAR} className="hidden md:block w-80 border-r border-border bg-card flex-shrink-0">
        <div className="sticky top-0">
          <AppSettingsSidebar active={activeTab} onTabChange={handleTabChange} tabs={filteredTabs} />
        </div>
      </div>
      {/* Main settings content */}
      <div className="flex-1 bg-background min-w-0">
        {(() => {
          const activeTabConfig: TabContentConfig = tabContent[activeTab] ?? { component: null, fullWidth: false };
          const fullWidth = Boolean(activeTabConfig.fullWidth);
          const spacingClasses = fullWidth ? 'px-0 sm:px-0' : 'px-4 sm:px-6 mt-12 pb-24';
          const widthClasses = fullWidth ? 'w-full max-w-none mx-0' : 'w-full max-w-[100vw] mx-auto';

          return (
            <div className={`${!fullWidth ? 'settings-page-content' : ''} ${widthClasses} ${spacingClasses}`}>
              {/* Tabs as horizontal nav for mobile only, hidden on md+ */}
              <div className="flex flex-col gap-2 mb-8 mt-4 md:hidden w-full overflow-x-hidden">
                {filteredTabs.map((tab) => {
                  if (tab.type === 'group' && tab.options) {
                    return (
                      <div key={tab.label} className="space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground px-2">{tab.label}</h3>
                        <div className="flex gap-2 overflow-x-auto pb-2 w-full max-w-[calc(100vw-2rem)]">
                          {tab.options.map((option) => (
                            <button
                              key={option.key}
                              className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors items-center gap-2 ${
                                activeTab === option.key
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                              onClick={() => handleTabChange(option.key)}
                              type="button"
                            >
                              {option.icon && <FontAwesomeIcon icon={option.icon} className="h-4 w-4" />}
                              <span>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={tab.key}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        activeTab === tab.key
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onClick={() => tab.key && handleTabChange(tab.key)}
                      type="button"
                    >
                      {tab.icon && <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />}
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="w-full h-full">
                <div id={
                  activeTab === 'account' ? TOUR_STEP_IDS.ACCOUNT_SECTION :
                  activeTab === 'theme' ? TOUR_STEP_IDS.THEME_SECTION :
                  activeTab === 'security' ? TOUR_STEP_IDS.SECURITY_SECTION :
                  activeTab === 'general' ? TOUR_STEP_IDS.GENERAL_SECTION :
                  activeTab === 'billing' ? TOUR_STEP_IDS.BILLING_SECTION :
                  activeTab === 'integrations' ? TOUR_STEP_IDS.INTEGRATIONS_SECTION :
                  activeTab === 'modules' ? TOUR_STEP_IDS.MODULES_SECTION :
                  activeTab === 'features' ? TOUR_STEP_IDS.FEATURES_SECTION :
                  activeTab === 'trustPage' ? TOUR_STEP_IDS.TRUST_PAGE_SECTION :
                  activeTab === 'partners' ? TOUR_STEP_IDS.PARTNERS_SECTION :
                  activeTab === 'menus' ? TOUR_STEP_IDS.MENUS_SECTION :
                  activeTab === 'calendar' ? TOUR_STEP_IDS.CALENDAR_SECTION :
                  activeTab === 'mspSettings' ? TOUR_STEP_IDS.MSP_SETTINGS_SECTION :
                  activeTab === 'diagnostics' ? TOUR_STEP_IDS.DIAGNOSTICS_SECTION :
                  activeTab === 'developer' ? TOUR_STEP_IDS.DEVELOPER_SECTION :
                  undefined
                }>
                  {activeTabConfig.component}
                </div>
              </div>
            </div>
          );
        })()}
        {/* <h1 className="text-3xl font-bold mb-2 text-foreground">Settings</h1> */}
      </div>
      <TourAlertDialog 
        isOpen={openTour} 
        setIsOpen={setOpenTour} 
      />
    </div>
  );
}
